import { create, StateCreator } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createSelectors } from './utils';

interface InboxState {
  chatInputHeight: number | null;
  setChatInputHeight: (height?: number | null) => void;
  /** null = the user has not folded or unfolded the composer this session yet. */
  chatInputCollapsed: boolean | null;
  setChatInputCollapsed: (collapsed: boolean) => void;
  /** Hands the next conversation back to the saved "start folded" preference. */
  resetChatInputCollapsed: () => void;
  composerFoldCount: number;
  incrementComposerFoldCount: () => void;
  /** Last resolved requester first name per conversation, so the folded strip never blanks. */
  stripRequesterFirstNames: Record<string, string>;
  rememberStripRequesterFirstName: (conversationId: string, firstName: string) => void;
}

const chatInputInitializer: StateCreator<InboxState> = (set) => ({
  chatInputHeight: null,
  setChatInputHeight: (height?: number | null) => set({ chatInputHeight: height ?? null }),
  chatInputCollapsed: null,
  setChatInputCollapsed: (collapsed: boolean) => set({ chatInputCollapsed: collapsed }),
  resetChatInputCollapsed: () => set({ chatInputCollapsed: null }),
  composerFoldCount: 0,
  incrementComposerFoldCount: () =>
    set((state) => ({ composerFoldCount: state.composerFoldCount + 1 })),
  stripRequesterFirstNames: {},
  rememberStripRequesterFirstName: (conversationId, firstName) =>
    set((state) =>
      state.stripRequesterFirstNames[conversationId] === firstName
        ? state
        : {
            stripRequesterFirstNames: {
              ...state.stripRequesterFirstNames,
              [conversationId]: firstName,
            },
          },
    ),
});

const useChatInputStoreRaw = create<InboxState, [['zustand/persist', unknown]]>(
  persist(
    (...args) => ({
      ...chatInputInitializer(...args),
    }),
    {
      name: 'pt-chat-input-height',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

export const useChatInputStore = createSelectors(useChatInputStoreRaw);

interface ComposerHintsState {
  /** The "double-click restores auto-size" cue is announced once, on the first manual resize. */
  resizeCueSeen: boolean;
  markResizeCueSeen: () => void;
}

const useComposerHintsStoreRaw = create<ComposerHintsState>()(
  persist(
    (set) => ({
      resizeCueSeen: false,
      markResizeCueSeen: () => set({ resizeCueSeen: true }),
    }),
    { name: 'pt-composer-hints' },
  ),
);

export const useComposerHintsStore = createSelectors(useComposerHintsStoreRaw);
